/* === src/pages/public/ProfilePage.js === */

import React, { useState, useEffect, useCallback } from 'react';
import { 
    Container, Typography, Card, CardContent, Box, Grid, TextField, Button, 
    CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, Divider,
    Avatar, Tab, Tabs, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
    List, ListItem, ListItemText, ListItemAvatar, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions
} from '@mui/material';
import { 
    PhotoCamera, Person as PersonIcon, Home as HomeIcon, 
    FamilyRestroom as GuardianIcon, School as SchoolIcon, 
    Save as SaveIcon, Delete as DeleteIcon, Add as AddIcon,
    Security as SecurityIcon, Business as BusinessIcon,
    AccountBalance as BankIcon, History as HistoryIcon,
    CheckCircle, Cancel
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// --- CONSTANTS ---
const PROVINCES = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"];
const COUNTRIES = ["South Africa", "Zimbabwe", "Namibia", "Botswana", "Mozambique", "Lesotho", "Eswatini", "Nigeria", "Kenya", "United Kingdom", "United States"];
const MATRIC_SUBJECTS = [
    "Afrikaans FAL", "Afrikaans HL", "English FAL", "English HL", "Setswana HL",
    "IsiZulu HL", "SiXhosa HL", "Sesotho HL", "Sepedi HL", "Life Orientation",
    "Life Science", "Biology", "Geography", "Accounting", "Business Studies",
    "Physical Science", "Mathematics", "Maths Literacy", "Engineering Graphics and Design",
    "Electrical Technology", "Economics", "Tourism", "Agriculture Science",
    "Dramatic Arts", "Religious Studies", "Computer Applications Technology"
];
const BANK_NAMES = ["Absa", "Capitec", "FNB", "Nedbank", "Standard Bank", "TymeBank", "Discovery Bank", "Bidvest", "Investec", "Other"];
const ACCOUNT_TYPES = ["Savings", "Cheque", "Transmission"];

// --- UTILS: SA ID PARSER ---
// --- UTILS: SA ID PARSER (Checksum Removed for easier testing) ---
const parseSAID = (idNumber) => {
    // Just ensure it is exactly 13 digits
    if (!idNumber || idNumber.length !== 13 || !/^\d{13}$/.test(idNumber)) {
        return { valid: false };
    }

    let yy = idNumber.substring(0, 2);
    let mm = idNumber.substring(2, 4);
    let dd = idNumber.substring(4, 6);
    
    // Safely determine 1900s vs 2000s based on the current year
    let currentYY = parseInt(new Date().getFullYear().toString().substring(2, 4));
    let year = parseInt(yy) <= currentYY ? `20${yy}` : `19${yy}`; 
    
    let genderCode = parseInt(idNumber.substring(6, 10));
    let gender = genderCode >= 5000 ? 'Male' : 'Female';

    return { valid: true, dob: `${year}-${mm}-${dd}`, gender };
};

// Helper to safely format timestamps without the +2 hour UTC shift
const formatLogDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    try {
        // Replace space with 'T' if missing and check for timezone offset
        let formattedStr = String(dateString).replace(' ', 'T');
        
        // If the backend returns a MySQL string without 'Z' or offset, append 'Z' for accurate UTC-to-Local conversion
        // or parse directly if stored as local time
        const d = new Date(formattedStr);
        if (isNaN(d.getTime())) return String(dateString);
        
        return d.toLocaleString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    } catch (e) {
        return String(dateString);
    }
};

const formatForDateInput = (dateString) => {
    if (!dateString) return '';
     try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return '';
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
        } catch (e) {
        return String(dateString);
    }
};

const ActivityTimeline = ({ logs }) => (
    <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflow: 'auto' }}>
        {(!logs || logs.length === 0) && <Typography variant="body2" color="text.secondary">No recent activity.</Typography>}
        {logs && logs.map((log, index) => {
            const logDate = log.AttemptedAt || log.ChangedAt || log.Timestamp; 
            const isSuccess = log.Status !== undefined ? log.Status : (log.ChangeType === 'LOGIN_SUCCESS');

            return (
                <React.Fragment key={index}>
                    <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                            <Avatar sx={{ bgcolor: isSuccess ? 'success.main' : 'error.main' }}>
                               {isSuccess ? <CheckCircle /> : <Cancel />}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                            primary={isSuccess ? 'Login Successful' : 'Login Failed'}
                            secondary={
                                <React.Fragment>
                                    <Typography sx={{ display: 'inline' }} component="span" variant="body2" color="text.primary">
                                        {formatLogDate(logDate)}
                                    </Typography>
                                    {` — IP: ${log.IpAddress || 'Unknown'}`}
                                </React.Fragment>
                            }
                        />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                </React.Fragment>
            );
        })}
    </List>
);

const ProfilePage = () => {
    const { user, token, updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tabIndex, setTabIndex] = useState(0);
    const [msg, setMsg] = useState({ type: '', text: '' });

    // 🚨 DETECT REDIRECT INTERCEPTION VIA THE DASHBOARD FUNNEL
    // --- DYNAMIC TABS BASED ON ROLE ---
    let tabs = [];
    if (user?.role === 'Applicant') {
        tabs = ['Personal', 'Guardian', 'Address', 'Academic', 'Banking', 'Security'];
    } else if (user?.role === 'SuperUser') {
        tabs = ['Personal', 'Security']; 
    } else {
        tabs = ['Personal', 'Company', 'Security']; 
    }

    // 🚨 DETECT REDIRECT INTERCEPTION AND AUTO-SWITCH TABS
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            
            if (params.get('reason') === 'incomplete_profile') {
                setMsg({ 
                    type: 'warning', 
                    text: '🔒 Profile Completion Required: Please complete your personal profile information (including Gender, Race, and ID Number) before starting or continuing your bursary application.' 
                });
            }

            // 🚨 THE FIX: Listen for the 'section' param and switch the tab automatically!
            const section = params.get('section');
            if (section) {
                // Find the index of the requested section (ignoring case)
                const targetIndex = tabs.findIndex(t => t.toLowerCase() === section.toLowerCase());
                if (targetIndex !== -1) {
                    setTabIndex(targetIndex);
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.role]); // Re-run if the user role changes so tabs are accurate

    // Data State
    const [profile, setProfile] = useState({});
    const [guardian, setGuardian] = useState({});
    const [address, setAddress] = useState({});
    const [academic, setAcademic] = useState([]);
    const [banking, setBanking] = useState({});
    const [company, setCompany] = useState({});
    const [activityLog, setActivityLog] = useState([]);

    const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '', totpCode: '' });
    const [mfaDialog, setMfaDialog] = useState({ open: false, qrCode: '', secret: '', code: '', loading: false });
    const [hasMfa, setHasMfa] = useState(false); // We need to know if they already have it!

    // --- FETCH DATA ---
    const fetchAllData = useCallback(async () => {
        if (!token) return; 
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const safeGet = (url, defaultVal) => api.get(url, { headers }).then(res => res.data).catch(() => defaultVal);

            const profileData = await safeGet('/applicant/profile', {});
            const logData = await safeGet('/auth/activity', []);

            setProfile(profileData || {});
            setActivityLog(logData || []);

            // 🚀 THE SYNC FIX: If TotpSecret is present in the profile payload, flip the UI to green!
            if (profileData && profileData.TotpSecret && String(profileData.TotpSecret).trim().length > 5) {
                setHasMfa(true);
            } else {
                setHasMfa(false);
            }

            if (user?.role === 'Applicant') {
                const [g, a, ac, b] = await Promise.all([
                    safeGet('/applicant/guardian', {}),
                    safeGet('/applicant/address', {}),
                    safeGet('/applicant/academic', []),
                    safeGet('/applicant/banking', {})
                ]);
                setGuardian(g);
                setAddress(a);
                // 🚨 THE FIX: Force any legacy/undefined levels to default to 'Matric'
                setAcademic(Array.isArray(ac) ? ac.map(item => ({ ...item, Level: item.Level || 'Matric' })) : []);
                setBanking(b);
            } else if (user?.role !== 'SuperUser') {
                // 🚨 FIX: We are now correctly calling the Company endpoint, not the Applicant endpoint!
                const c = await safeGet('/company/my-company', {});
                setCompany(c || {});
            }
        } catch (err) {
            console.error("Fetch Data Error:", err);
            setMsg({ type: 'error', text: 'Failed to load profile data.' });
        } finally {
            setLoading(false);
        }
    }, [token, user?.role]);

    useEffect(() => { fetchAllData(); }, [fetchAllData]);

    const handleIdChange = (e) => {
        const val = e.target.value;
        const isSA = (profile.Citizenship || 'South Africa') === 'South Africa';

        setProfile(prev => {
            let updated = { ...prev, IdNumber: val };
            if (isSA) {
                updated.DateOfBirth = '';
                updated.Gender = '';
                if (val && val.length === 13) {
                    const { valid, dob, gender } = parseSAID(val);
                    if (valid) {
                        updated.DateOfBirth = dob;
                        updated.Gender = gender;
                        updated.IdType = 'SA_ID';
                    }
                }
            }
            return updated;
        });
    };

    // --- GENERIC IMAGE UPLOADER ---
    const uploadImage = async (file, type) => {
        if (!file) return;
        setSaving(true);
        const formData = new FormData();
        // You can use the same route or create a specific '/applicant/cover' route on the backend
        formData.append('avatar', file); 
        
        try {
            // Note: Make sure your backend saves 'CoverImage' if type === 'cover'
            // For now, we hit the avatar route. You may need to create a dedicated cover route!
            const route = type === 'cover' ? '/applicant/cover' : '/applicant/avatar';
            const res = await api.post(route, formData, { 
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } 
            });
            const newImageSrc = res.data.path || res.data.url;
            
            if (type === 'cover') {
                setProfile(prev => ({ ...prev, CoverImage: newImageSrc }));
            } else {
                setProfile(prev => ({ ...prev, ProfilePicture: newImageSrc }));
                if(updateUser) updateUser({ profilePicture: newImageSrc });
            }
            setMsg({ type: 'success', text: 'Image updated successfully.' });
        } catch (err) { 
            setMsg({ type: 'error', text: 'Upload failed. Ensure file size is under limits.' }); 
        } finally { 
            setSaving(false); 
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMsg({ type: '', text: '' });
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const currentTab = tabs[tabIndex];

            if (currentTab === 'Personal') {
                 const payload = { ...profile, DateOfBirth: formatForDateInput(profile.DateOfBirth) || null };
                 await api.put('/applicant/profile', payload, { headers });
            }
            if (currentTab === 'Guardian') await api.put('/applicant/guardian', guardian, { headers });
            if (currentTab === 'Address') await api.put('/applicant/address', address, { headers });
            if (currentTab === 'Academic') await api.put('/applicant/academic', { results: academic }, { headers });
            if (currentTab === 'Banking') await api.put('/applicant/banking', banking, { headers });
            
            if (currentTab === 'Company') {
                // Save Company data
                await api.put('/company/my-company', company, { headers });
            }
            
            if (currentTab === 'Security') {
                if(passwords.new !== passwords.confirm) throw new Error("Passwords do not match");
                await api.put('/auth/change-password', passwords, { headers });
                setPasswords({ current: '', new: '', confirm: '' });
            }

            setMsg({ type: 'success', text: 'Saved successfully.' });
            fetchAllData(); 
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.message || err.message || 'Save failed.' });
        } finally {
            setSaving(false);
        }
    };

    const handleInitiateMfa = async () => {
        try {
            setMfaDialog(prev => ({ ...prev, loading: true, open: true }));
            const res = await api.get('/auth/mfa/setup', { headers: { Authorization: `Bearer ${token}` } });
            setMfaDialog({ open: true, qrCode: res.data.qrCodeDataUrl, secret: res.data.secret, code: '', loading: false });
        } catch (error) {
            setMsg({ type: 'error', text: 'Failed to generate QR code.' });
            setMfaDialog({ open: false, qrCode: '', secret: '', code: '', loading: false });
        }
    };

    const handleConfirmMfa = async () => {
        try {
            await api.post('/auth/mfa/verify-setup', { 
                secret: mfaDialog.secret, 
                token: mfaDialog.code 
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            setMsg({ type: 'success', text: 'Authenticator App linked successfully!' });
            setHasMfa(true);
            setMfaDialog({ open: false, qrCode: '', secret: '', code: '', loading: false });
        } catch (error) {
            // 🚨 THE FIX: Use inline UI messaging instead of an alert() popup
            setMsg({ type: 'error', text: error.response?.data?.message || 'Invalid code. Please try again.' });
            // Keep the modal open but clear the code so they can re-type
            setMfaDialog(prev => ({ ...prev, code: '' }));
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
            
            {/* --- NEW CORPORATE HEADER REDESIGN --- */}
            <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden', mb: 4 }}>
                {/* 1. Cover Image Banner */}
                <Box sx={{ 
                    height: { xs: 150, md: 220 }, 
                    width: '100%', 
                    bgcolor: 'grey.300', 
                    backgroundImage: `url(${profile.CoverImage || 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=1000&q=80'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative'
                }}>
                    <IconButton 
                        component="label" 
                        sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(255,255,255,0.8)', '&:hover': {bgcolor: 'white'} }}
                        disabled={saving}
                    >
                        <PhotoCamera />
                        {/* Note: Requires a /applicant/cover backend route to be wired up! */}
                        <input hidden accept="image/*" type="file" onChange={(e) => uploadImage(e.target.files[0], 'cover')} />
                    </IconButton>
                </Box>

                {/* 2. Overlapping Profile Picture & Info */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-end' }, px: 4, pb: 4, position: 'relative' }}>
                    
                    <Box sx={{ mt: { xs: -8, sm: -6 }, position: 'relative', zIndex: 2 }}>
                        <Avatar 
                            src={profile.ProfilePicture} 
                            sx={{ width: 140, height: 140, border: '4px solid white', bgcolor: 'primary.main', fontSize: '3rem', boxShadow: 3 }}
                        >
                            {profile.FirstNames?.[0]}
                        </Avatar>
                        <IconButton 
                            component="label" 
                            sx={{ position: 'absolute', bottom: 4, right: 4, bgcolor: 'white', boxShadow: 2, '&:hover': {bgcolor: 'grey.100'} }}
                            disabled={saving}
                        >
                            <PhotoCamera fontSize="small"/>
                            <input hidden accept="image/*" type="file" onChange={(e) => uploadImage(e.target.files[0], 'avatar')} />
                        </IconButton>
                    </Box>

                    <Box sx={{ pt: 2, ml: { xs: 0, sm: 3 }, textAlign: { xs: 'center', sm: 'left' }, flexGrow: 1 }}>
                        <Typography variant="h4" fontWeight="bold">{profile.FirstNames} {profile.Surname}</Typography>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 1 }}>
                            <BusinessIcon fontSize="small" /> {user?.role} • {profile.Email || user?.email}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Card elevation={2} sx={{ borderRadius: 3 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                    <Tabs value={tabIndex} onChange={(e, v) => { setTabIndex(v); setMsg({type:'', text:''}); }} variant="scrollable" scrollButtons="auto">
                        {tabs.map((tab, index) => (
                            <Tab key={index} label={tab} iconPosition="start" sx={{ fontWeight: 'bold' }} icon={
                                tab === 'Personal' ? <PersonIcon /> :
                                tab === 'Guardian' ? <GuardianIcon /> :
                                tab === 'Address' ? <HomeIcon /> :
                                tab === 'Academic' ? <SchoolIcon /> :
                                tab === 'Banking' ? <BankIcon /> :
                                tab === 'Company' ? <BusinessIcon /> :
                                <SecurityIcon />
                            } />
                        ))}
                    </Tabs>
                </Box>
                
                <CardContent sx={{ p: 4 }}>
                    {msg.text && <Alert severity={msg.type} sx={{ mb: 3 }}>{msg.text}</Alert>}

                    {/* PERSONAL */}
                    {tabs[tabIndex] === 'Personal' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Citizenship</InputLabel>
                                    <Select 
                                        value={profile.Citizenship || 'South Africa'} 
                                        label="Citizenship"
                                        onChange={(e) => setProfile(p => ({ ...p, Citizenship: e.target.value, IdType: e.target.value === 'South Africa' ? 'SA_ID' : 'Passport', IdNumber: '' }))}
                                    >
                                        {COUNTRIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth required
                                    label={((profile.Citizenship || 'South Africa') === 'South Africa') ? 'SA ID Number' : 'Passport Number'}
                                    value={profile.IdNumber || ''}
                                    onChange={handleIdChange}
                                    helperText={((profile.Citizenship || 'South Africa') === 'South Africa') ? 'DOB & Gender auto-detected' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="First Names" value={profile.FirstNames || ''} onChange={(e) => setProfile({...profile, FirstNames: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Surname" value={profile.Surname || ''} onChange={(e) => setProfile({...profile, Surname: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth 
                                    label="Date of Birth" 
                                    type="date" 
                                    InputLabelProps={{ shrink: true }}
                                    value={formatForDateInput(profile.DateOfBirth)} 
                                    disabled={(profile.Citizenship || 'South Africa') === 'South Africa'}
                                    onChange={(e) => setProfile({...profile, DateOfBirth: e.target.value})}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Gender" value={profile.Gender || ''} disabled={(profile.Citizenship || 'South Africa') === 'South Africa'} onChange={(e) => setProfile({...profile, Gender: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Phone Number" value={profile.CellNumber || ''} onChange={(e) => setProfile({...profile, CellNumber: e.target.value})} /></Grid>
                        </Grid>
                    )}

                    {/* GUARDIAN */}
                    {tabs[tabIndex] === 'Guardian' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Relationship" value={guardian.Relation || ''} onChange={(e) => setGuardian({...guardian, Relation: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Full Name" value={guardian.FirstName || ''} onChange={(e) => setGuardian({...guardian, FirstName: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Cell Number" value={guardian.CellNumber || ''} onChange={(e) => setGuardian({...guardian, CellNumber: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={guardian.Email || ''} onChange={(e) => setGuardian({...guardian, Email: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Employer" value={guardian.Employer || ''} onChange={(e) => setGuardian({...guardian, Employer: e.target.value})} /></Grid>
                        </Grid>
                    )}

                    {/* ADDRESS */}
                    {tabs[tabIndex] === 'Address' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}><TextField fullWidth label="Street Address" value={address.Street || ''} onChange={(e) => setAddress({...address, Street: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Suburb" value={address.Suburb || ''} onChange={(e) => setAddress({...address, Suburb: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="City" value={address.City || ''} onChange={(e) => setAddress({...address, City: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Postal Code" value={address.PostalCode || ''} onChange={(e) => setAddress({...address, PostalCode: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Province</InputLabel>
                                    <Select value={address.Province || ''} label="Province" onChange={(e) => setAddress({...address, Province: e.target.value})}>
                                        {PROVINCES.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {/* ACADEMIC */}
                    {tabs[tabIndex] === 'Academic' && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Academic Results</Typography>
                                <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={() => setAcademic([...academic, { Subject: '', Grade: '', Level: 'Matric' }])}>
                                    Add Result
                                </Button>
                            </Box>
                            <Alert severity="info" sx={{ mb: 2 }}>You can capture both your Matric subjects and current Tertiary modules here.</Alert>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={150}>Level</TableCell>
                                        <TableCell>Subject / Module Name</TableCell>
                                        <TableCell width={150}>Percentage (%)</TableCell>
                                        <TableCell width={50}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {academic.map((row, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <Select fullWidth size="small" value={row.Level || 'Matric'} onChange={(e) => { const n = [...academic]; n[i].Level = e.target.value; setAcademic(n); }}>
                                                    <MenuItem value="Matric">Matric</MenuItem>
                                                    <MenuItem value="Tertiary">Tertiary</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                {row.Level === 'Tertiary' ? (
                                                    <TextField fullWidth size="small" placeholder="Enter module name" value={row.Subject || ''} onChange={(e) => { const n = [...academic]; n[i].Subject = e.target.value; setAcademic(n); }} />
                                                ) : (
                                                    <Select fullWidth size="small" value={row.Subject || ''} displayEmpty onChange={(e) => { const n = [...academic]; n[i].Subject = e.target.value; setAcademic(n); }}>
                                                        <MenuItem value="" disabled>Select Subject</MenuItem>
                                                        {MATRIC_SUBJECTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                                    </Select>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextField type="number" size="small" value={row.Grade || ''} onChange={(e) => { const n = [...academic]; n[i].Grade = e.target.value; setAcademic(n); }} />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton color="error" size="small" onClick={() => setAcademic(academic.filter((_, idx) => idx !== i))}><DeleteIcon /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {/* BANKING */}
                    {tabs[tabIndex] === 'Banking' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                {banking.BankVerified ? 
                                    <Alert severity="success" icon={<CheckCircle />}>Banking details verified. Locked.</Alert> : 
                                    <Alert severity="warning" icon={<Cancel />}>Banking details pending verification.</Alert>
                                }
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Bank Name</InputLabel>
                                    <Select value={banking.BankName || ''} label="Bank Name" disabled={banking.BankVerified} onChange={(e) => setBanking({...banking, BankName: e.target.value})}>
                                        {BANK_NAMES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Account Number" value={banking.AccountNumber || ''} disabled={banking.BankVerified} onChange={(e) => setBanking({...banking, AccountNumber: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Branch Code" value={banking.BranchCode || ''} disabled={banking.BankVerified} onChange={(e) => setBanking({...banking, BranchCode: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Account Type</InputLabel>
                                    <Select value={banking.AccountType || 'Savings'} label="Account Type" disabled={banking.BankVerified} onChange={(e) => setBanking({...banking, AccountType: e.target.value})}>
                                        {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}><TextField fullWidth label="Account Holder Name" value={banking.AccountHolderName || ''} disabled={banking.BankVerified} onChange={(e) => setBanking({...banking, AccountHolderName: e.target.value})} /></Grid>
                        </Grid>
                    )}

                    {/* COMPANY TAB */}
                    {tabs[tabIndex] === 'Company' && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}><Typography variant="h6" color="primary" fontWeight="bold">Organization Details</Typography></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Company Name" value={company.CompanyName || ''} onChange={(e) => setCompany({...company, CompanyName: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Registration Number" value={company.RegistrationNumber || ''} onChange={(e) => setCompany({...company, RegistrationNumber: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Tax Number" value={company.TaxReferenceNumber || ''} onChange={(e) => setCompany({...company, TaxReferenceNumber: e.target.value})} /></Grid>
                            
                            <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="h6" color="primary" fontWeight="bold">Contact & Address</Typography></Grid>
                            
                            <Grid item xs={12} sm={6}>
                                {/* 🚨 FIX: Clarified the purpose of this email! */}
                                <TextField 
                                    fullWidth 
                                    label="Billing & Official Contact Email" 
                                    value={company.ContactEmail || ''} 
                                    onChange={(e) => setCompany({...company, ContactEmail: e.target.value})} 
                                    helperText="All invoices and official platform notices will be sent here."
                                />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Contact Phone" value={company.ContactPhone || ''} onChange={(e) => setCompany({...company, ContactPhone: e.target.value})} /></Grid>
                            <Grid item xs={12}><TextField fullWidth label="Street Address" value={company.Address || ''} onChange={(e) => setCompany({...company, Address: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="City" value={company.City || ''} onChange={(e) => setCompany({...company, City: e.target.value})} /></Grid>
                            <Grid item xs={12} sm={6}><TextField fullWidth label="Postal Code" value={company.PostalCode || ''} onChange={(e) => setCompany({...company, PostalCode: e.target.value})} /></Grid>
                        </Grid>
                    )}

                    {/* SECURITY */}
                    {tabs[tabIndex] === 'Security' && (
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Change Password</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField label="Current Password" type="password" fullWidth value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})} />
                                    <TextField label="New Password" type="password" fullWidth value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})} />
                                    <TextField label="Confirm Password" type="password" fullWidth value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})} />
                                    
                                    <Divider sx={{ my: 1 }} />
                                    
                                    <Typography variant="body2" color="text.secondary">
                                        Enter your 6-digit Authenticator app code to verify this change.
                                    </Typography>
                                    <TextField 
                                        label="TOTP Code (Authenticator)" 
                                        fullWidth 
                                        required
                                        value={passwords.totpCode} 
                                        onChange={(e) => setPasswords({...passwords, totpCode: e.target.value.replace(/\D/g, '')})} 
                                        inputProps={{ maxLength: 6 }} 
                                    />
                                </Box>
                                {/* MFA SETUP SECTION */}
                                <Box sx={{ 
                                    mt: 4, 
                                    p: 3, 
                                    border: '1px solid', 
                                    borderColor: 'divider', // Automatically adapts to light/dark mode
                                    borderRadius: 2, 
                                    bgcolor: (theme) => hasMfa 
                                        ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'success.light') // Soft green in dark mode, standard in light
                                        : (theme.palette.mode === 'dark' ? 'background.default' : 'grey.50') // Adapts to dark background
                                }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SecurityIcon color={hasMfa ? "success" : "inherit"} /> 
                                        Two-Factor Authentication (MFA)
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
                                        {hasMfa 
                                            ? "Your account is currently protected by an Authenticator App." 
                                            : "Enhance your account security by linking an Authenticator app (like Google Authenticator or Authy)."}
                                    </Typography>
                                    
                                    {!hasMfa && (
                                        <Button variant="contained" color="primary" onClick={handleInitiateMfa}>
                                            Setup Authenticator App
                                        </Button>
                                    )}
                                </Box>
                            </Grid>

                             <Grid item xs={12} md={6}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom><HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Recent Login Activity</Typography>
                                <ActivityTimeline logs={activityLog} />
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained" 
                            size="large" 
                            startIcon={<SaveIcon />} 
                            onClick={handleSave}
                            disabled={saving || (tabs[tabIndex] === 'Banking' && banking.BankVerified)}
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* --- MFA SETUP DIALOG --- */}
            <Dialog 
                open={mfaDialog.open} 
                onClose={() => setMfaDialog(prev => ({ ...prev, open: false }))}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Set Up Two-Factor Authentication</DialogTitle>
                <DialogContent>
                    {mfaDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="body1" gutterBottom>
                                1. Open your Authenticator app (Google Authenticator, Authy, etc.).
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                                2. Scan the QR code below:
                            </Typography>
                            
                            {mfaDialog.qrCode && (
                                <Box 
                                    component="img" 
                                    src={mfaDialog.qrCode} 
                                    alt="MFA QR Code" 
                                    sx={{ width: 200, height: 200, mx: 'auto', my: 2, border: '1px solid #ccc', borderRadius: 2 }}
                                />
                            )}
                            
                            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                                3. Enter the 6-digit code generated by your app to verify.
                            </Typography>
                            
                            <TextField
                                autoFocus
                                margin="dense"
                                label="6-Digit Authenticator Code"
                                type="text"
                                fullWidth
                                variant="outlined"
                                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                                value={mfaDialog.code}
                                onChange={(e) => setMfaDialog(prev => ({ ...prev, code: e.target.value.replace(/\D/g, '') }))}
                                sx={{ mt: 2 }}
                            />
                        </Box>
                    )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={() => setMfaDialog(prev => ({ ...prev, open: false }))} color="inherit">
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleConfirmMfa} 
                            variant="contained" 
                            color="primary"
                            disabled={mfaDialog.code.length !== 6}
                        >
                            Verify & Enable
                        </Button>
                    </DialogActions>
                </Dialog>
        </Container>
    );
};

export default ProfilePage;
